/* eslint-disable react/jsx-props-no-spreading */
import * as ReactDOMClient from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type FC, useCallback, useContext, useEffect, useRef } from 'react';
import { useArticleListingQuery, type ArticleListingQuery } from 'cms/cms';
import ArticleCard from '3-components/ArticleCard/ArticleCard';
import { resolveArticleListing } from 'cms/resolvers/articleListingResolver';
import ArticleListingProvider from './ArticleListingProvider';
import ArticleListingContext, { type ArticleListingContextProps, type ArticleListingProps } from './ArticleListingContext';
import { getQueryURL, getFilterQuery, getWhereValue, type WhereItem, getPageQueryURL } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import { DEFAULT_FETCH_PARAMS, OPTMIZELY_GRAPHQL_BASE_URL } from 'cms/constants';
import ListingHeader from '3-components/ListingApp/ListingHeader/ListingHeader';
import ListingContent from '3-components/ListingApp/ListingContent/ListingContent';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import { type ActiveFilter } from '3-components/ListingApp/ListingFilters/ListingFiltersTypes';


const ArticleListing: FC = () => {
    const {
        locale,
        activeFilters,
        setActiveFilters,
        listing: {
            filters,
            itemsPerPage = 10,
            filterByAncestors,
        },
        page,
        setPage,
        label,
        cms,
    } = useContext(ArticleListingContext) as ArticleListingContextProps;

    const updateFilters = (value: ActiveFilter[]): void => {
        setPage(0);
        setActiveFilters(value);
    };

    const { search, yearpublished, ...restParams } = getFilterQuery(activeFilters, filters);
    const scrollToEl = useRef<HTMLDivElement>(null);

    const whereItems: WhereItem[] = [];
    if (search) {
        whereItems.push({
            type: 'search',
            field: '_fulltext',
            value: search,
        });
    }

    if (yearpublished) {
        whereItems.push({
            type: 'year',
            field: 'ArticlePublishDate',
            value: yearpublished,
        });
    }

    if (filterByAncestors) {
        whereItems.push({
            type: 'ancestors',
            field: 'Ancestors',
            value: filterByAncestors,
        });
    }

    const where = getWhereValue(whereItems);

    const { data, error, isLoading, isError, isFetching } = useArticleListingQuery({ endpoint: `${OPTMIZELY_GRAPHQL_BASE_URL}${cms.apiKey}`, fetchParams: DEFAULT_FETCH_PARAMS }, {
        locale,
        where,
        ...restParams,
        skip: page * itemsPerPage,
        limit: itemsPerPage,
    }, {

        queryKey: ['article-cards'],
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
        select: useCallback((result: ArticleListingQuery) => {
            return resolveArticleListing(result, { minReadSuffix: label.minReadSuffix, minWatchSuffix: label.minWatchSuffix });
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []), // https://tkdodo.eu/blog/react-query-data-transformations
    });

    useEffect(() => {
        window.history.replaceState({}, '', getQueryURL(activeFilters, filters));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    useEffect(() => {
        if (scrollToEl?.current) {
            scrollToEl.current.scrollIntoView({ behavior: 'smooth' });
        }

        window.history.replaceState({}, '', getPageQueryURL(page));
    }, [page]);

    if (isLoading) {
        return (
            <ListingLoader loadingMessage={label.loadingMessage} />
        );
    }

    if (isError || !data) {
        console.error(error);

        return (
            <ListingError errorMessage={label.errorMessage} />
        );
    }

    return (
        <div className="listing-app" ref={scrollToEl}>
            <div className="container">
                {label.title && (
                    <ListingHeader
                        filters={filters}
                        activeFilters={activeFilters}
                        setActiveFilters={updateFilters}
                        title={label.title}
                        clearFilterLabel={label.clearFilterButton}
                    />
                )}
                <ListingContent
                    isFetching={isFetching}
                    isNoItems={!data.items.length}
                    pagination={{
                        itemsPerPage,
                        page,
                        setPage,
                        totalItems: data.total,
                    }}
                    loadingLabel={label.loadingMessage}
                    noItemsLabel={label.noItemMessage}
                    cols="3"

                >
                    {data.items.map(props => {
                        return (
                            <ArticleCard key={props.title} {...props} size={null} />
                        );
                    })}
                </ListingContent>
            </div>
        </div>
    );
};

const queryClient = new QueryClient();

interface ArticleListingInitProps {
    el: HTMLDivElement;
    props: ArticleListingProps;
}

const ArticleListingInit = ({ el, props }: ArticleListingInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <ArticleListingProvider {...props}>
                <ArticleListing />
            </ArticleListingProvider>
        </QueryClientProvider>
    );
};

export default ArticleListingInit;
